import { createSlice } from '@reduxjs/toolkit';
// utils
import { Api } from '../../utils/api';
import { BLOG_URLS } from '../../utils/api/urls';
import { normalizeBlogPost, normalizeBlogPosts } from '../../utils/blog';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: false,
  posts: [],
  post: null,
  relatedPosts: [],
  totalCount: 0,
  totalPages: 1,
  currentPage: 1,
};

const slice = createSlice({
  name: 'blog',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET POSTS
    getPostsSuccess(state, action) {
      state.isLoading = false;
      state.error = false;
      state.posts = action.payload.posts;
      state.totalCount = action.payload.totalCount;
      state.totalPages = action.payload.totalPages;
      state.currentPage = action.payload.currentPage;
    },

    // GET POST
    getPostSuccess(state, action) {
      state.isLoading = false;
      state.error = false;
      state.post = action.payload.post;
      state.relatedPosts = action.payload.relatedPosts;
    },
  },
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

// Blog catalogue for the blogs page. The backend filters, searches and sorts
// server-side (`category`, `q`, `sort`) and paginates only when `page` is
// present, so the page passes its UI state straight through.
export function getBlogPosts({ category, query, sort, page, pageSize } = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const params = {};

      if (category) {
        params.category = category;
      }

      if (query) {
        params.q = query;
      }

      if (sort) {
        params.sort = sort;
      }

      if (page) {
        params.page = page;
      }

      if (pageSize) {
        params.pageoffset = pageSize;
      }

      const hasParams = Object.keys(params).length > 0;
      const response = await Api.unget(BLOG_URLS.list, hasParams ? params : undefined);
      const data = response.data ?? {};
      const rows = Array.isArray(data) ? data : data.results ?? [];
      const links = !Array.isArray(data) && data.links ? data.links : {};

      dispatch(
        slice.actions.getPostsSuccess({
          posts: normalizeBlogPosts(rows),
          totalCount: Number.isFinite(links.count) ? links.count : rows.length,
          totalPages: Number.isFinite(links.pages) && links.pages > 0 ? links.pages : 1,
          currentPage: Number.isFinite(links.current) && links.current > 0 ? links.current : 1,
        })
      );
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

// Single article plus its related block. The backend returns
// `{ results, related }`; both use the same normaliser so missing covers or
// null keyword lists can never break the cards.
export function getBlogPost(slug) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await Api.unget(`${BLOG_URLS.get}${slug}/`);
      const data = response.data ?? {};

      dispatch(
        slice.actions.getPostSuccess({
          post: normalizeBlogPost(data.results),
          relatedPosts: normalizeBlogPosts(data.related),
        })
      );
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

